import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const ProtectedRoute = ({ children }) => {
  const { user, loading, checkInitialSetupStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingSetup, setCheckingSetup] = useState(false);
  const hasCheckedSetup = useRef(false);

  useEffect(() => {
    if (!loading && !user) {
      // Small delay to ensure smooth navigation
      const timer = setTimeout(() => {
        navigate('/login');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, loading, navigate]);

  // Check initial setup status when user is authenticated and not on initial-setup page
  useEffect(() => {
    // Reset check flag when pathname changes to /initial-setup
    if (location.pathname === '/initial-setup') {
      hasCheckedSetup.current = false;
      return;
    }

    // Only check if we haven't checked yet, user is loaded, and not already checking
    if (!loading && user && location.pathname !== '/initial-setup' && !checkingSetup && !hasCheckedSetup.current) {
      console.log('[ProtectedRoute] Checking initial setup status...');
      setCheckingSetup(true);
      hasCheckedSetup.current = true;
      
      checkInitialSetupStatus().then((status) => {
        console.log('[ProtectedRoute] Setup status:', status);
        if (status) {
          if (status.needsSetup || status.needsModeSelection) {
            console.log('[ProtectedRoute] Redirecting to /initial-setup');
            navigate('/initial-setup', { replace: true });
            // Reset flag after navigation so it can check again if needed
            hasCheckedSetup.current = false;
          } else {
            console.log('[ProtectedRoute] Setup complete, allowing access');
          }
        } else {
          console.warn('[ProtectedRoute] No status returned, assuming setup needed for safety');
          // If status is null/undefined, assume setup is needed for safety
          navigate('/initial-setup', { replace: true });
          hasCheckedSetup.current = false;
        }
        setCheckingSetup(false);
      }).catch((err) => {
        console.error('[ProtectedRoute] Error checking setup status:', err);
        // On error, redirect to setup for safety
        navigate('/initial-setup', { replace: true });
        setCheckingSetup(false);
        hasCheckedSetup.current = false;
      });
    }
  }, [user, loading, location.pathname, navigate, checkInitialSetupStatus]);

  if (loading || checkingSetup) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    // Don't render anything while redirecting
    return null;
  }

  return children;
};

export default ProtectedRoute; 